import { useState, FC, FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, DollarSign, Tag, Box, Ruler } from 'lucide-react';
import { useUIStore } from '@/stores';
import { ServiceCategory as Category, Service, Unit } from '@/shared/model';
import { ResponsiveModal } from '@/shared/ui/responsive-modal';
import { Button } from '@/shared/ui/base';

interface CreateServiceModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreate: (data: Partial<Service>) => void;
  categories: Category[];
  units?: Unit[];
  onCreateCategory?: () => void;
}

export const CreateServiceModal: FC<CreateServiceModalProps> = ({ 
    isOpen, onClose, onCreate, categories, units = [], onCreateCategory 
}) => {
  const { t } = useTranslation();
  const currency = useUIStore((s) => s.currency);
  const [formData, setFormData] = useState({
    name: '',
    categoryId: categories[0]?.id || '',
    unitType: 'pcs',
    priceLabor: ''
  });

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!formData.name) return;
    
    onCreate({
      name: formData.name,
      categoryId: formData.categoryId || categories[0]?.id || '',
      unit: formData.unitType,
      priceLabor: parseFloat(formData.priceLabor) || 0,
      unitType: formData.unitType
    });
    
    setFormData({ name: '', categoryId: categories[0]?.id || '', unitType: 'pcs', priceLabor: '' });
    onClose();
  };

  return (
    <ResponsiveModal
        open={isOpen}
        onOpenChange={(open) => !open && onClose()}
        title={t('services.create_modal.title', 'Новая услуга')}
        className="max-w-md"
    >
      <form onSubmit={handleSubmit} className="space-y-5">
        
        {/* Name */}
        <div className="space-y-1">
          <label className="text-xs font-bold text-slate-400 dark:text-slate-500 uppercase ml-1">{t('services.create_form.name_label', 'Название услуги')}</label>
          <input 
            autoFocus
            type="text"
            placeholder={t('services.create_form.name_placeholder', 'Например: Монтаж люстры')}
            value={formData.name}
            onChange={(e) => setFormData({...formData, name: e.target.value})}
            className="w-full pl-4 pr-4 py-3 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl focus:border-blue-500 focus:bg-white dark:focus:bg-slate-800 outline-none font-bold text-slate-700 dark:text-slate-100 transition-all"
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
           {/* Category */}
           <div className="space-y-1">
              <div className="flex justify-between items-center ml-1">
                  <label className="text-xs font-bold text-slate-400 dark:text-slate-500 uppercase flex items-center gap-1">
                  <Tag size={12}/> {t('services.create_form.category_label', 'Категория')}
                  </label>
                  {onCreateCategory && (
                      <button 
                          type="button" 
                          onClick={onCreateCategory}
                          className="text-[10px] font-bold text-blue-600 hover:underline"
                      >
                          +{t('common.new', 'Новая')}
                      </button>
                  )}
              </div>
              <div className="relative">
                 <select 
                    value={formData.categoryId}
                    onChange={(e) => setFormData({...formData, categoryId: e.target.value})}
                    className="w-full pl-3 pr-8 py-3 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl focus:border-blue-500 outline-none font-bold text-slate-700 dark:text-slate-100 appearance-none transition-all text-sm"
                 >
                    {categories.map(c => (
                      <option key={c.id} value={c.id} className="dark:bg-slate-900">
                          {c.nameKey ? t(c.nameKey, c.name) : c.name}
                      </option>
                    ))}
                 </select>
                 <div className="absolute right-3 top-3.5 pointer-events-none text-slate-400">
                    <Box size={16}/>
                 </div>
              </div>
           </div>

           {/* Unit */}
           <div className="space-y-1">
              <label className="text-xs font-bold text-slate-400 dark:text-slate-500 uppercase ml-1 flex items-center gap-1">
                 <Ruler size={12}/> {t('services.create_form.unit_label', 'Ед. изм.')}
              </label>
              <select 
                  value={formData.unitType}
                  onChange={(e) => setFormData({...formData, unitType: e.target.value})}
                  className="w-full px-3 py-3 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl focus:border-blue-500 outline-none font-bold text-slate-700 dark:text-slate-100 transition-all text-sm"
              >
                  {units.map(u => (
                      <option key={u.id} value={u.id} className="dark:bg-slate-900">
                          {u.nameKey ? t(u.nameKey, u.name) : u.name}
                      </option>
                  ))}
              </select>
           </div>
        </div>

        {/* Price */}
        <div className="space-y-1">
           <label className="text-xs font-bold text-slate-400 dark:text-slate-500 uppercase ml-1 flex items-center gap-1">
              <DollarSign size={12}/> {t('services.create_form.price_label', 'Цена ({{currency}})', { currency })}
           </label>
           <input 
              type="number"
              min="0"
              step="0.01"
              placeholder="0.00"
              value={formData.priceLabor}
              onChange={(e) => setFormData({...formData, priceLabor: e.target.value})}
              className="w-full pl-4 pr-4 py-3 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl focus:border-blue-500 focus:bg-white dark:focus:bg-slate-800 outline-none font-bold text-slate-800 dark:text-slate-100 text-lg transition-all"
           />
        </div>

        {/* Submit */}
        <Button
           type="submit"
           disabled={!formData.name}
           variant="primary"
           className="w-full h-14 font-bold text-lg mt-4"
        >
           <Check size={20}/> {t('services.create_form.submit_btn', 'Создать услугу')}
        </Button>

      </form>
    </ResponsiveModal>
  );
};

export default CreateServiceModal;
